import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AuthService } from '../../service/auth.service';

export interface UserCreate {
  username: string;
  email: string;
  password: string;
  telephone?: string;
  role?: string;
}

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    ToastModule
  ],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.css']
})
export class RegisterComponent {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() registerSuccess = new EventEmitter<void>();

  registerForm: FormGroup;
  isLoading = false;
  hidePassword = true;
  
  // Wizard properties
  currentStep = 1;
  showSuccessAnimation = false;
  
  // Password strength properties
  passwordStrength: 'weak' | 'medium' | 'strong' = 'weak';
  hasMinLength = false;
  hasUpperCase = false;
  hasLowerCase = false;
  hasNumber = false;

  roles = [
    { value: 'USER', label: 'Utilisateur' },
    { value: 'ADMIN', label: 'Administrateur' }
  ];

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService
  ) {
    this.registerForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      email: ['', [Validators.required, Validators.email]],
      telephone: [''],
      password: ['', [Validators.required, Validators.minLength(6)]],
      role: ['USER', Validators.required]
    });
  }

  onSubmit() {
    if (this.registerForm.valid) {
      this.isLoading = true;
      const userData: UserCreate = this.registerForm.value;

      this.authService.register(userData).subscribe({
        next: (response) => {
          // Show success animation
          this.showSuccessAnimation = true;
          
          // After 2 seconds, close modal and emit success
          setTimeout(() => {
            this.messageService.add({
              severity: 'success',
              summary: 'Succès',
              detail: 'Compte créé avec succès ! Vous pouvez maintenant vous connecter.',
              life: 5000
            });
            this.registerSuccess.emit();
            this.closeModal();
            this.resetForm();
            this.showSuccessAnimation = false;
          }, 2000);
        },
        error: (error) => {
          console.error('Erreur lors de l\'inscription:', error);
          const errorMessage = error.error?.detail || 'Une erreur est survenue lors de l\'inscription';
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: errorMessage,
            life: 5000
          });
          this.isLoading = false;
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched() {
    Object.keys(this.registerForm.controls).forEach(key => {
      const control = this.registerForm.get(key);
      control?.markAsTouched();
    });
  }

  closeModal() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.currentStep = 1;
  }

  resetForm() {
    this.registerForm.reset({
      role: 'USER'
    });
    this.hidePassword = true;
    this.currentStep = 1;
    this.passwordStrength = 'weak';
    this.hasMinLength = false;
    this.hasUpperCase = false;
    this.hasLowerCase = false;
    this.hasNumber = false;
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  // Wizard navigation methods
  nextStep() {
    if (this.canProceedToNextStep()) {
      this.currentStep++;
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  canProceedToNextStep(): boolean {
    if (this.currentStep === 1) {
      const username = this.registerForm.get('username');
      const email = this.registerForm.get('email');
      return !!(username?.valid && email?.valid);
    } else if (this.currentStep === 2) {
      const password = this.registerForm.get('password');
      return !!(password?.valid && this.hasMinLength);
    }
    return false;
  }

  // Password strength checker
  checkPasswordStrength() {
    const password = this.registerForm.get('password')?.value || '';

    this.hasMinLength = password.length >= 6;
    this.hasUpperCase = /[A-Z]/.test(password);
    this.hasLowerCase = /[a-z]/.test(password);
    this.hasNumber = /[0-9]/.test(password);

    let score = 0;
    if (this.hasMinLength) score++;
    if (this.hasUpperCase) score++;
    if (this.hasLowerCase) score++;
    if (this.hasNumber) score++;

    if (score <= 2) {
      this.passwordStrength = 'weak';
    } else if (score === 3) {
      this.passwordStrength = 'medium';
    } else {
      this.passwordStrength = 'strong';
    }
  }

  getPasswordStrengthText(): string {
    const texts = {
      weak: 'Faible',
      medium: 'Moyenne',
      strong: 'Forte'
    };
    return texts[this.passwordStrength];
  }

  // Role selection
  selectRole(role: string) {
    this.registerForm.patchValue({ role });
  }

  getRoleLabel(): string {
    const role = this.roles.find(r => r.value === this.registerForm.get('role')?.value);
    return role?.label || 'Utilisateur';
  }

  getRoleIcon(roleValue: string): string {
    const icons: { [key: string]: string } = {
      USER: 'person',
      ADMIN: 'admin_panel_settings'
    };
    return icons[roleValue] || 'person';
  }

  getRoleDescription(roleValue: string): string {
    const descriptions: { [key: string]: string } = {
      USER: 'Accès standard aux fonctionnalités',
      ADMIN: 'Accès complet à toutes les fonctionnalités'
    };
    return descriptions[roleValue] || '';
  }
}
